//@author: mikhail
import { DataAccess } from "../../../../dataaccess/data.access";
import { Cache } from "../../../../decorators/cache.decorator";
import { PreferenceService } from "../../../../services/preference.service";
import { S25Util } from "../../../../util/s25-util";

/** Matches legacy `DashPanelService_CAPACITY_BREAKPOINTS` in panel-service.js */
export const DASH_PANEL_SERVICE_CAPACITY_BREAKPOINTS = (
    "0,5,10,15,20,25,30,40,50,75,100,150,200,300,500,1000,10000,100000" as const
).split(",");

export class DashPanelService {
    public static async _get(panelId: number | string) {
        const data = await DataAccess.get(
            DataAccess.injectCaller(`/home/dash/panel-items.json?panelId=${panelId}`, "DashPanelService._get"),
        );
        return data.panel.items;
    }

    public static async _getSpaceSearchesCollections() {
        const data = await DataAccess.get(
            DataAccess.injectCaller(
                "/home/dash/panel-space-searches-collections.json",
                "DashPanelService._getSpaceSearchesCollections",
            ),
        );
        return data.spaceSearchesCollections;
    }

    public static _putIsCollapsed(jsonPayload: string) {
        return DataAccess.put(
            DataAccess.injectCaller("/home/dash/panel-iscollapsed.json", "DashPanelService._putIsCollapsed"),
            jsonPayload,
        );
    }

    public static async getSpaceCapBreakpoints(): Promise<string[]> {
        const resp = await PreferenceService.getPreferences(["SpaceCapBreak"]);
        const rawValue = (resp.SpaceCapBreak?.value ?? "").trim().replace(/[^0-9,]/g, "");
        const caps = rawValue.split(",").filter(S25Util.isDefined).sort(S25Util.sortNumeric());
        if (caps?.length >= 2) {
            return caps;
        }
        return [...DASH_PANEL_SERVICE_CAPACITY_BREAKPOINTS];
    }

    @Cache({ targetName: "DashPanelService" })
    public static async get(panelId: number | string, _typeId: number | string) {
        return DashPanelService._get(panelId);
    }

    @Cache({ targetName: "DashPanelService", cachedMethod: "get" })
    public static refresh(panelId: number | string, typeId: number | string) {
        return DashPanelService.get(panelId, typeId);
    }

    public static preloadTasks() {
        return DashPanelService.get(9, 1010);
    }

    public static getSpaceSearchesCollections() {
        return DashPanelService._getSpaceSearchesCollections();
    }

    public static async getCapacity(number: number | string) {
        const caps = await DashPanelService.getSpaceCapBreakpoints();
        return S25Util.findBoundaries(caps, S25Util.toInt(number));
    }

    public static async hasCapacityFor(candidateCapacity: number | string, direction: "next" | "prev") {
        const caps = await DashPanelService.getSpaceCapBreakpoints();
        const numericCapacity = S25Util.toInt(candidateCapacity);
        return direction === "next"
            ? numericCapacity < S25Util.toInt(caps[caps.length - 1])
            : numericCapacity > S25Util.toInt(caps[0]);
    }

    public static isNextCapacity(currentMaxCapacity: number | string) {
        return DashPanelService.hasCapacityFor(currentMaxCapacity, "next");
    }

    public static isPrevCapacity(currentMinCapacity: number | string) {
        return DashPanelService.hasCapacityFor(currentMinCapacity, "prev");
    }

    public static async putIsCollapsed(panelId: number | string, isCollapsed: boolean): Promise<null> {
        const payload = S25Util.toJson({ panel: { panelId, isCollapsed } });
        await DashPanelService._putIsCollapsed(payload);
        return null;
    }
}
